import { useQuery } from '@apollo/client'
import PhoneIcon from '@mui/icons-material/Phone'
import SearchIcon from '@mui/icons-material/Search'
import SwapCallsIcon from '@mui/icons-material/SwapCalls'
import {
  Avatar,
  Box,
  Chip,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Radio,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import axios from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { User } from 'src/@types/graphql'
import { Button } from 'src/components'
import { useUser } from 'src/contexts/providers/UserProvider'
import { useVoice } from 'src/contexts/providers/VoiceProvider'
import {
  env,
  formatPhoneNumber,
  formatUserName,
  fuzzySearch,
} from 'src/helpers'
import { GET_USERS } from '../data/queries'
import useTransferCallStatus from '../hooks/useTransferCallStatus'
import { Container, Row } from './TransferCall.styles'

type Steps = 'LIST' | 'ENTER_PHONE' | 'CALL_WAITING' | 'CALL_IN_SESSION'

const outgoingConferenceUrl = `${env(
  'rest_api_endpoint',
)}/voice/outgoing-conference`
const cancelTransfereUrl = `${env('rest_api_endpoint')}/voice/cancel-transfer`
const completeTransfereUrl = `${env(
  'rest_api_endpoint',
)}/voice/complete-transfer`

const TransferAdminItem = ({ admin, selectedAdmin, handleChange }) => {
  const name = formatUserName(admin)

  const isAway = !admin.isAvailable

  const disabled = !admin.enable_phone_transfers || isAway

  return (
    <Box className="list" key={admin.id}>
      <Box className="container">
        <Avatar>{name?.substring(0, 1)}</Avatar>
        <Typography className="name">{name}</Typography>
      </Box>
      <Row>
        <Radio
          disabled={disabled}
          checked={selectedAdmin?.id === admin.id}
          onChange={() => handleChange({ ...admin })}
          value={admin.id}
          name="radio-buttons"
          inputProps={{ 'aria-label': admin?.id.toString() }}
        />
      </Row>
    </Box>
  )
}

const TransferCallAdminList = ({
  selectedAdmin,
  searchTerm,
  setSelectedValue,
}) => {
  const { user } = useUser()

  const { loading, data } = useQuery(GET_USERS)

  const admins = (data?.users || [])
    .slice()
    .sort((a: User, b: User) => a.first_name.localeCompare(b.first_name))
    .filter((a) => a.id !== user?.id)

  const filteredAdmins = useMemo(
    () => fuzzySearch(['email', 'first_name', 'last_name'], admins, searchTerm),
    [admins, searchTerm],
  )

  return (
    <>
      {filteredAdmins.map((admin) => (
        <TransferAdminItem
          key={admin.id}
          admin={admin}
          selectedAdmin={selectedAdmin}
          handleChange={setSelectedValue}
        />
      ))}
      {loading && <div>loading...</div>}
    </>
  )
}

const TransferCall = ({ setActiveAction }) => {
  const theme = useTheme()
  const { call, resetTransfer } = useVoice()
  const { user } = useUser()
  const [step, setStep] = useState<Steps>('LIST')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAdmin, setSelectedAdmin] = useState<
    (User & { isOffline: Boolean }) | null
  >(null)
  const [dialedPhone, setDialedPhone] = useState('')
  const [transferCallSid, setTransferCallSid] = useState('')

  const conferenceId = call?.person?.conference_id
  const params = call.params
  const callSid = call.callSid
  const transferFromCallSid = call.transferFromCallSid

  const handlePhoneSelect = useCallback(() => {
    setSelectedAdmin(null)
    setStep('ENTER_PHONE')
  }, [])

  const handleListSelect = useCallback(() => {
    setDialedPhone('')
    setStep('LIST')
  }, [])

  const handleSearch = useCallback((term) => {
    setSearchTerm(term)
  }, [])

  const handlePhoneChange = useCallback((event) => {
    setDialedPhone(event.target.value)
  }, [])

  const handleReset = useCallback(() => {
    resetTransfer()
    setActiveAction(null)
  }, [resetTransfer, setActiveAction])

  const handleCallAction = useCallback(
    async (url) => {
      try {
        await axios.post(url, {
          // when receiving transfer, the to callSid is the sid of the current call
          toCallSid: transferFromCallSid ? callSid : transferCallSid,
          // when receiving transfer, the from callSid is the sid of the transferring call
          fromCallSid: transferFromCallSid ? transferFromCallSid : callSid,
          conferenceId,
        })
      } catch (error) {
        console.log(error)
      }
      handleReset()
    },
    [callSid, conferenceId, handleReset, transferCallSid, transferFromCallSid],
  )

  const handleCancelTransfer = useCallback(
    async () => handleCallAction(cancelTransfereUrl),
    [handleCallAction],
  )

  const handleCompleteTransfer = useCallback(
    async () => handleCallAction(completeTransfereUrl),
    [handleCallAction],
  )

  const handleTransferCall = useCallback(async () => {
    try {
      const currentPerson = call.person
      const fromName = (user && formatUserName(user)) || ''
      const toName = selectedAdmin
        ? formatUserName(selectedAdmin)
        : formatPhoneNumber(dialedPhone)
      const newParams = new URLSearchParams(params)
      newParams.set('fromName', fromName)
      newParams.set('toName', toName)
      newParams.set('callType', 'transfer')
      newParams.set('fromCallSid', callSid || '')
      newParams.set('type', currentPerson?.type || '')
      newParams.set('person_id', currentPerson?.person_id || '')
      newParams.set('first_name', currentPerson?.first_name || '')
      newParams.set('last_name', currentPerson?.last_name || '')
      newParams.set('property_name', currentPerson?.property_name || '')
      newParams.set('property_phone', currentPerson?.property_phone || '')
      newParams.set('conference_id', currentPerson?.conference_id || '')

      const toSelected = selectedAdmin?.isOffline
        ? selectedAdmin?.phone_number
        : `client:${selectedAdmin?.twilio_worker_id}`
      const to = selectedAdmin ? toSelected : dialedPhone

      const query = newParams.toString()
      const { data } = await axios.post(`${outgoingConferenceUrl}?${query}`, {
        from: `client:${user?.twilio_worker_id}`,
        to,
        conferenceId,
      })
      setTransferCallSid(data?.callSid)
      if (data.status === 'queued') {
        setStep('CALL_WAITING')
      } else {
        alert('Call could not be initiated. Please check your inputs')
      }
    } catch (error) {
      console.log(error)
    }
  }, [
    call.person,
    callSid,
    conferenceId,
    dialedPhone,
    params,
    selectedAdmin,
    user,
  ])

  const transferCall = useTransferCallStatus(
    transferCallSid || call.transferFromCallSid,
  )

  useEffect(() => {
    if (transferCall) {
      switch (transferCall.status) {
        case 'in-progress':
          {
            setStep('CALL_IN_SESSION')
          }
          break
        case 'no-answer':
          {
            setStep(selectedAdmin ? 'LIST' : 'ENTER_PHONE')
          }
          break
        case 'completed':
          {
            // reset transfer info
            handleReset()
          }
          break
        default:
      }
    }
  }, [handleReset, selectedAdmin, setActiveAction, transferCall])

  return (
    <Container>
      {(step === 'LIST' || step === 'ENTER_PHONE') && (
        <Box className="top-content">
          <Typography className="title">Transfer Call</Typography>

          <Box className="actions">
            <Box className="search-container">
              {step === 'LIST' && (
                <FormControl variant="outlined">
                  <OutlinedInput
                    id="outlined-adornment-search"
                    type="search"
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon color="primary" />
                      </InputAdornment>
                    }
                    placeholder="Search for admin..."
                    inputProps={{
                      'aria-label': 'Search',
                    }}
                    size="small"
                    onChange={(e) => handleSearch(e.currentTarget.value)}
                  />
                </FormControl>
              )}
            </Box>

            {step === 'ENTER_PHONE' ? (
              <Button variant="outlined" onClick={handleListSelect}>
                Select Admin
              </Button>
            ) : (
              <Button variant="outlined" onClick={handlePhoneSelect}>
                Enter Phone Number
              </Button>
            )}
            <Button
              onClick={handleTransferCall}
              disabled={!selectedAdmin && !dialedPhone}
            >
              Transfer Call
            </Button>
          </Box>
        </Box>
      )}

      <Box className="content">
        {step === 'LIST' && (
          <TransferCallAdminList
            searchTerm={searchTerm}
            selectedAdmin={selectedAdmin}
            setSelectedValue={setSelectedAdmin}
          />
        )}

        {step === 'ENTER_PHONE' && (
          <Box className="input-cont">
            <TextField
              sx={{ mr: 1, width: '100%' }}
              variant="standard"
              label="Phone Number"
              placeholder="Enter a Phone Number"
              onChange={handlePhoneChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {step === 'CALL_WAITING' && (
          <Box className="call-waiting">
            <Box className="icon-info">
              <PhoneIcon />
            </Box>
            <Box className="info">
              <Typography className="name-title">
                {selectedAdmin
                  ? formatUserName(selectedAdmin)
                  : formatPhoneNumber(dialedPhone, 'US', true)}
              </Typography>
              <Chip label="Online" color={'success'} />
            </Box>
            <Typography className="text-info">
              Waiting for {selectedAdmin ? 'admin' : 'phone number'} to pick
              up...
            </Typography>
            <Button
              variant="outlined"
              textColor={theme.palette.error.main}
              borderColor={theme.palette.error.main}
              onClick={handleCancelTransfer}
            >
              Cancel Transfer
            </Button>
          </Box>
        )}

        {step === 'CALL_IN_SESSION' && user && (
          <Box className="call-in-session">
            <Box className="swap">
              <Box className="swap-container">
                <Box className="icon-info">K</Box>
                <Typography className="name-title">
                  {call.transferFromName || formatUserName(user)}
                </Typography>
              </Box>
              <Box className="swap-icon">
                <SwapCallsIcon />
              </Box>
              <Box className="swap-container">
                <Box className="icon-info">
                  <PhoneIcon />
                </Box>
                <Typography className="name-title">
                  {call.transferToName ||
                    (selectedAdmin
                      ? formatUserName(selectedAdmin)
                      : formatPhoneNumber(dialedPhone, 'US', true))}
                </Typography>
              </Box>
            </Box>

            <Box className="btn-container">
              <Button
                variant="outlined"
                textColor={theme.palette.error.main}
                borderColor={theme.palette.error.main}
                onClick={handleCancelTransfer}
              >
                Cancel Transfer
              </Button>
              <Button onClick={handleCompleteTransfer}>
                Complete Transfer
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  )
}

export default TransferCall
